import React,{useState,useEffect,useRef} from 'react';
import Layout from '../components/layout/Layout';
import PatientSearchInput from '../components/patient/PatientSearchInput';
import {useAuth} from '../hooks/useAuth';
import api from '../services/api';
import {useNavigate} from 'react-router-dom';
import {fmtDate,fmtDateTime,calcAge,safeArray} from '../utils/helpers';
import {getHospitalInfo} from '../utils/printUtils';
import {getCurrencySymbol} from '../utils/locale';
import {useTranslation} from 'react-i18next';
import TranslateButton from '../components/common/TranslateButton';
import AutoTranslatedText from '../components/common/AutoTranslatedText';
import AutoTranslatedNote from '../components/common/AutoTranslatedNote';
import i18n from '../i18n';
import {autoTranslateBatch} from '../utils/autoTranslate';
import FileShaClaimButton from '../components/common/FileShaClaimButton';

const CONDITION_OPTIONS=['STABLE','IMPROVED','UNCHANGED','DETERIORATED','CRITICAL'];
const DISPOSITION_OPTIONS=['HOME','REFERRED','DAMA','LAMA','EXPIRED','TRANSFERRED'];

const emptyForm=()=>({
  patientId:'',admissionId:'',doctorId:'',
  admissionDate:'',dischargeDate:new Date().toISOString().slice(0,16),
  chiefComplaints:'',clinicalFindings:'',provisionalDiagnosis:'',finalDiagnosis:'',
  icdCodes:[] as string[],
  proceduresPerformed:'',hospitalCourse:'',investigationResults:'',
  medicationsAdministered:[] as any[],
  dischargeMedications:[] as any[],
  conditionAtDischarge:'STABLE',dischargeDisposition:'HOME',
  followUpInstructions:'',followUpDate:'',
  dietAdvice:'',activityAdvice:'',warningSigns:'',
  status:'DRAFT',
});

/* Drug search for discharge medication list (formulary search) */
function DrugSearchInput({onSelect}:{onSelect:(d:any)=>void}){
  const {t}=useTranslation();
  const [q,setQ]=useState('');
  const [results,setResults]=useState<any[]>([]);
  const [loading,setLoading]=useState(false);
  const [open,setOpen]=useState(false);
  const ref=useRef<HTMLDivElement>(null);

  useEffect(()=>{
    const h=(e:MouseEvent)=>{if(ref.current&&!ref.current.contains(e.target as Node))setOpen(false);};
    document.addEventListener('mousedown',h);return()=>document.removeEventListener('mousedown',h);
  },[]);

  useEffect(()=>{
    if(q.length<2){setResults([]);setOpen(false);return;}
    const t=setTimeout(async()=>{
      setLoading(true);
      try{
        const r=await api.getDrugs({search:q,take:10});
        const list=Array.isArray(r.data?.drugs)?r.data.drugs:safeArray(r.data,['drugs']);
        setResults(list);setOpen(true);
      }catch{setResults([]);}finally{setLoading(false);}
    },300);
    return()=>clearTimeout(t);
  },[q]);

  const pick=(d:any)=>{setQ('');setResults([]);setOpen(false);onSelect(d);};

  return(
    <div ref={ref} className="relative">
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">💊</span>
        <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400 pl-9" value={q} onChange={e=>setQ(e.target.value)}
          onFocus={()=>q.length>=2&&setOpen(true)} placeholder={t('discharge.searchDrugPlaceholder','Search drug from formulary to add...')} autoComplete="off"/>
        {loading&&<div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-green-200 border-t-green-600 rounded-full animate-spin"/>}
      </div>
      {open&&results.length>0&&(
        <div className="absolute z-50 top-full left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-xl mt-1 max-h-60 overflow-y-auto">
          {results.map((d:any)=>(
            <div key={d.id} onClick={()=>pick(d)} className="px-3 py-2 hover:bg-green-50 cursor-pointer border-b border-slate-100 last:border-b-0 flex items-center justify-between">
              <div>
                <p className="font-medium text-sm text-slate-800">{d.name||d.drugName}{d.strength?` (${d.strength})`:''}</p>
                <p className="text-xs text-slate-400">{d.genericName||d.composition||''}</p>
              </div>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${(+d.totalStock||0)>0?'bg-green-100 text-green-700':'bg-red-100 text-red-700'}`}>
                {(+d.totalStock||0)>0?t('discharge.inStock','In Stock'):t('discharge.outOfStock','Out of Stock')}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

async function printDischargeSummary(ds:any,patient:any,doctor:any){
  const h=await getHospitalInfo();
  const fullName=(patient.firstName||patient.first_name||'')+' '+(patient.lastName||patient.last_name||'');
  const icd=(ds.icdCodes||[]).join(', ');
  // Same auto-translate-on-display treatment as the on-screen read-only view (AutoTranslatedNote)
  // — the clinician's free-text fields AND medication names get machine-translated (cached
  // LibreTranslate) into the current UI language before being put into the print HTML. Falls
  // back to the original text untouched if the UI language is English or translation fails.
  const lang=(i18n.language||'en').split('-')[0];
  const NOTE_FIELDS=['chiefComplaints','clinicalFindings','provisionalDiagnosis','finalDiagnosis','proceduresPerformed','hospitalCourse','investigationResults','dietAdvice','activityAdvice','warningSigns','followUpInstructions'] as const;
  const drugNames=[...(ds.dischargeMedications||[]),...(ds.medicationsAdministered||[])].map((m:any)=>m.drugName||m.name||m.medication_name||m.medicationName).filter(Boolean);
  const tr=lang==='en'?{}:await autoTranslateBatch([...NOTE_FIELDS.map(f=>ds[f]).filter(Boolean),...drugNames],lang);
  const tx=(v:string|undefined)=>v?(tr[v]||v):v;
  const meds=(ds.dischargeMedications||[]).map((m:any,i:number)=>`
    <tr><td>${i+1}</td><td><strong>${tx(m.drugName||m.name)}</strong>${m.strength?` (${m.strength})`:''}</td>
    <td>${m.dosage||'—'}</td><td>${m.frequency||'—'}</td><td>${m.duration||'—'}</td><td>${m.instructions||'—'}</td></tr>`).join('');
  const admMeds=(ds.medicationsAdministered||[]).map((m:any)=>`<li>${tx(m.name||m.medication_name||m.medicationName)||''} ${m.dosage?`- ${m.dosage}`:''} ${m.frequency?`(${m.frequency})`:''}</li>`).join('');
  // Static section headers/labels — translated via i18next's own t() (i18n.t works outside React
  // components/hooks too), reusing the SAME discharge.* keys already used for these labels
  // on-screen, and the same CONDITION_OPTIONS/DISPOSITION_OPTIONS values this page uses.
  const L=(key:string,fallback:string,opts?:any):string=>String(i18n.t(key,fallback,opts));
  const STATUS_LABEL=(s:string)=>s==='FINALIZED'?L('discharge.statusFinalized','FINALIZED'):L('discharge.statusDraft','DRAFT');
  const CONDITION_LABEL=(o:string):string=>(({
    STABLE:L('discharge.conditionStable','STABLE'),IMPROVED:L('discharge.conditionImproved','IMPROVED'),
    UNCHANGED:L('discharge.conditionUnchanged','UNCHANGED'),DETERIORATED:L('discharge.conditionDeteriorated','DETERIORATED'),
    CRITICAL:L('discharge.conditionCritical','CRITICAL'),
  } as Record<string,string>)[o]||o||'—');
  const DISPOSITION_LABEL=(o:string):string=>(({
    HOME:L('discharge.dispositionHome','HOME'),REFERRED:L('discharge.dispositionReferred','REFERRED'),
    DAMA:L('discharge.dispositionDama','DAMA'),LAMA:L('discharge.dispositionLama','LAMA'),
    EXPIRED:L('discharge.dispositionExpired','EXPIRED'),TRANSFERRED:L('discharge.dispositionTransferred','TRANSFERRED'),
  } as Record<string,string>)[o]||o||'—');
  const html=`<!DOCTYPE html><html><head><title>${L('discharge.pageTitle','Discharge Summary')}</title>
  <style>body{font-family:Arial,sans-serif;padding:24px;font-size:13px;color:#1e293b;}
  .header{text-align:center;border-bottom:2px solid #000;padding-bottom:10px;margin-bottom:12px;}
  .row{display:flex;justify-content:space-between;margin:4px 0;font-size:12px;}
  .section{margin:10px 0;}
  .section h4{margin:0 0 4px;font-size:13px;border-bottom:1px solid #ccc;padding-bottom:2px;}
  table{width:100%;border-collapse:collapse;margin-top:6px;}
  th{background:#333;color:#fff;padding:6px;text-align:left;font-size:11px;}
  td{padding:6px;border-bottom:1px solid #eee;font-size:12px;}
  .footer{margin-top:40px;display:flex;justify-content:space-between;}
  .sign{text-align:center;}.sign-line{border-top:1px solid #000;width:180px;margin-bottom:4px;}
  @media print{body{padding:10px;}}</style></head><body>
  <div class="header"><h2>🏥 ${h.name}</h2><p>${L('discharge.pageTitle','Discharge Summary')} ${ds.summaryNumber?`#${ds.summaryNumber}`:''} ${ds.status?`· ${STATUS_LABEL(ds.status)}`:''}</p></div>
  <div class="row"><span><strong>${L('common.patient','Patient')}:</strong> ${fullName}</span><span><strong>${L('discharge.uhidLabel','UHID:')}</strong> ${patient.uhid||''}</span></div>
  <div class="row"><span><strong>${L('discharge.ageSex','Age/Sex:')}</strong> ${calcAge(patient.dateOfBirth||patient.date_of_birth)} / ${patient.gender||''}</span>
    <span><strong>${L('discharge.bloodGroup','Blood Group:')}</strong> ${patient.bloodGroup||patient.blood_group||'—'}</span></div>
  <div class="row"><span><strong>${L('discharge.admissionDate','Admission Date')}:</strong> ${fmtDate(ds.admissionDate)}</span>
    <span><strong>${L('discharge.dischargeDate','Discharge Date')}:</strong> ${fmtDate(ds.dischargeDate)}</span></div>

  <div class="section"><h4>${L('discharge.chiefComplaints','Chief Complaints')}</h4><p>${tx(ds.chiefComplaints)||'—'}</p></div>
  <div class="section"><h4>${L('discharge.clinicalFindings','Clinical Findings')}</h4><p>${tx(ds.clinicalFindings)||'—'}</p></div>
  <div class="section"><h4>${L('discharge.provisionalDiagnosis','Provisional Diagnosis')}</h4><p>${tx(ds.provisionalDiagnosis)||'—'}</p></div>
  <div class="section"><h4>${L('discharge.finalDiagnosis','Final Diagnosis')}</h4><p>${tx(ds.finalDiagnosis)||'—'}${icd?` (ICD: ${icd})`:''}</p></div>
  <div class="section"><h4>${L('discharge.proceduresPerformed','Procedures Performed')}</h4><p>${tx(ds.proceduresPerformed)||'—'}</p></div>
  <div class="section"><h4>${L('discharge.hospitalCourse','Hospital Course')}</h4><p>${tx(ds.hospitalCourse)||'—'}</p></div>
  <div class="section"><h4>${L('discharge.investigationResults','Investigation Results')}</h4><p>${tx(ds.investigationResults)||'—'}</p></div>
  ${admMeds?`<div class="section"><h4>${L('discharge.medsAdministered','Medications Administered During Stay')}</h4><ul>${admMeds}</ul></div>`:''}

  <div class="section"><h4>${L('discharge.conditionAtDischarge','Condition at Discharge')}</h4><p>${CONDITION_LABEL(ds.conditionAtDischarge)} — ${L('discharge.disposition','Disposition')}: ${DISPOSITION_LABEL(ds.dischargeDisposition)}</p></div>

  <div class="section"><h4>${L('discharge.dischargeMedications','Discharge Medications')}</h4>
  <table><thead><tr><th>#</th><th>${L('discharge.medicine','Medicine')}</th><th>${L('discharge.dosage','Dosage')}</th><th>${L('discharge.frequency','Frequency')}</th><th>${L('discharge.duration','Duration')}</th><th>${L('discharge.instructions','Instructions')}</th></tr></thead>
  <tbody>${meds||`<tr><td colspan="6" style="text-align:center">${L('discharge.nonePrescribed','None prescribed')}</td></tr>`}</tbody></table></div>

  <div class="section"><h4>${L('discharge.followUpInstructions','Follow-up Instructions')}</h4><p>${tx(ds.followUpInstructions)||'—'}${ds.followUpDate?` ${L('discharge.reviewOn','(Review on {{date}})',{date:fmtDate(ds.followUpDate)})}`:''}</p></div>
  ${ds.dietAdvice?`<div class="section"><h4>${L('discharge.dietAdvice','Diet Advice')}</h4><p>${tx(ds.dietAdvice)}</p></div>`:''}
  ${ds.activityAdvice?`<div class="section"><h4>${L('discharge.activityAdvice','Activity Advice')}</h4><p>${tx(ds.activityAdvice)}</p></div>`:''}
  ${ds.warningSigns?`<div class="section"><h4 style="color:#b91c1c">⚠️ ${L('discharge.warningSigns','⚠️ Warning Signs — When to Seek Immediate Care').replace(/^\W*/,'')}</h4><p>${tx(ds.warningSigns)}</p></div>`:''}

  <div class="footer">
    <div></div>
    <div class="sign"><div class="sign-line"></div><p>${L('emr.doctorPrefix','Dr.')} ${doctor?.firstName||''} ${doctor?.lastName||''}</p></div>
  </div>
  </body></html>`;
  const w=window.open('','_blank','width=850,height=900');
  if(!w)return;
  w.document.write(html);w.document.close();w.onload=()=>w.print();
}


function BillingSummaryCard({admissionId}:{admissionId:string}){
  const {t}=useTranslation();
  const[summary,setSummary]=React.useState<any>(null);
  React.useEffect(()=>{
    api.getIPDBillingSummary(admissionId).then((r:any)=>setSummary(r?.data||null)).catch(()=>{});
  },[admissionId]);
  if(!summary)return null;
  const bal=+(summary.outstandingBalance||summary.balanceAmount||0);
  const dep=+(summary.totalDeposits||0);
  const paid=+(summary.totalPaid||0);
  const total=+(summary.totalBilled||0);
  return(
    <div className="bg-white rounded-2xl border border-emerald-200 shadow-sm overflow-hidden">
      <div className="px-5 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <h3 className="font-bold">{t('discharge.billingSummary','💰 IPD Billing Summary')}</h3>
      </div>
      <div className="grid grid-cols-4 divide-x divide-slate-100 text-center">
        {[[t('discharge.totalBill','Total Bill'),getCurrencySymbol()+total.toLocaleString('en-KE'),'text-slate-800'],
          [t('common.paid','Paid'),getCurrencySymbol()+paid.toLocaleString('en-KE'),'text-emerald-600'],
          [t('discharge.deposit','Deposit'),getCurrencySymbol()+dep.toLocaleString('en-KE'),'text-blue-600'],
          [t('discharge.balanceDue','Balance Due'),getCurrencySymbol()+bal.toLocaleString('en-KE'),bal>0?'text-red-600':'text-emerald-600'],
        ].map(([l,v,c])=>(
          <div key={String(l)} className="p-3">
            <p className="text-xs text-slate-400 font-semibold uppercase">{l}</p>
            <p className={`text-lg font-black mt-0.5 ${c}`}>{v}</p>
          </div>
        ))}
      </div>
      {bal>0&&(
        <div className="px-5 py-3 bg-red-50 border-t border-red-100 text-center">
          <p className="text-sm text-red-700 font-semibold">{t('discharge.outstandingBalanceMsg','⚠️ Outstanding balance of {{amount}} — collect at billing counter before discharge',{amount:`${getCurrencySymbol()}${bal.toLocaleString('en-KE')}`})}</p>
        </div>
      )}
    </div>
  );
}
export default function DischargeSummaryPage(){
  const {t}=useTranslation();
  const {user}=useAuth();
  const [selectedPatient,setSelectedPatient]=useState<any>(null);
  const [admission,setAdmission]=useState<any>(null);
  const [admissionLoading,setAdmissionLoading]=useState(false);
  const [history,setHistory]=useState<any[]>([]);
  const [ds,setDs]=useState<any>(null); // active discharge summary record (saved)
  const [form,setForm]=useState<any>(emptyForm());
  const [editing,setEditing]=useState(false);
  const [saving,setSaving]=useState(false);
  const [msg,setMsg]=useState('');

  const onPatientSelect=async(p:any)=>{
    setSelectedPatient(p);setAdmission(null);setDs(null);setForm(emptyForm());setEditing(false);
    setAdmissionLoading(true);
    try{
      const [admRes,histRes]=await Promise.all([
        api.getCurrentAdmission(p.id).catch(()=>null),
        api.getDischargeByPatient(p.id).catch(()=>null),
      ]);
      const adm=admRes?.data;
      setAdmission(adm&&adm.id?adm:null);
      setHistory(safeArray(histRes?.data));
    }catch{}
    finally{setAdmissionLoading(false);}
  };

  const onPatientClear=()=>{
    setSelectedPatient(null);setAdmission(null);setDs(null);setHistory([]);setForm(emptyForm());setEditing(false);
  };

  const startFromAdmission=async()=>{
    if(!admission)return;
    setSaving(true);setMsg('');
    try{
      // Check if a summary already exists for this admission
      const existing=await api.getDischargeByAdmission(admission.id).catch(()=>null);
      if(existing?.data?.id){
        setDs(existing.data);
        setForm({...emptyForm(),...existing.data,
          admissionDate:existing.data.admissionDate?String(existing.data.admissionDate).slice(0,16):'',
          dischargeDate:existing.data.dischargeDate?String(existing.data.dischargeDate).slice(0,16):'',
          followUpDate:existing.data.followUpDate?String(existing.data.followUpDate).slice(0,10):'',
          icdCodes:existing.data.icdCodes||[],
          medicationsAdministered:existing.data.medicationsAdministered||[],
          dischargeMedications:existing.data.dischargeMedications||[],
        });
        setEditing(existing.data.status!=='FINALIZED');
        setMsg(t('discharge.loadedExisting','Loaded existing discharge summary for this admission.'));
      }else{
        const draft=await api.generateDischargeDraft(admission.id);
        const d=draft.data||{};
        setDs(null);
        setForm({...emptyForm(),...d,
          patientId:selectedPatient.id,
          doctorId:d.doctorId||user?.id||'',
          admissionDate:d.admission_date||d.admissionDate?String(d.admission_date||d.admissionDate).slice(0,16):'',
          dischargeDate:d.discharge_date||d.dischargeDate?String(d.discharge_date||d.dischargeDate).slice(0,16):new Date().toISOString().slice(0,16),
          icdCodes:d.icdCodes||[],
          medicationsAdministered:d.medicationsAdministered||[],
          dischargeMedications:d.dischargeMedications||[],
        });
        setEditing(true);
        setMsg(t('discharge.draftGenerated','Draft generated from admission & visit records. Review and save.'));
      }
    }catch(e:any){setMsg('Error: '+(e.response?.data?.message||e.message));}
    finally{setSaving(false);setTimeout(()=>setMsg(''),4000);}
  };

  const openExisting=(item:any)=>{
    setDs(item);
    setForm({...emptyForm(),...item,
      admissionDate:item.admission_date||item.admissionDate?String(item.admission_date||item.admissionDate).slice(0,16):'',
      dischargeDate:item.discharge_date||item.dischargeDate?String(item.discharge_date||item.dischargeDate).slice(0,16):'',
      followUpDate:item.followUpDate?String(item.followUpDate).slice(0,10):'',
      icdCodes:item.icdCodes||[],
      medicationsAdministered:item.medicationsAdministered||[],
      dischargeMedications:item.dischargeMedications||[],
    });
    setEditing(item.status!=='FINALIZED');
  };

  const reloadHistory=async()=>{
    if(!selectedPatient)return;
    try{const h=await api.getDischargeByPatient(selectedPatient.id);setHistory(safeArray(h.data));}catch{}
  };

  const save=async(finalize=false)=>{
    setSaving(true);setMsg('');
    try{
      const payload={
        ...form,
        patientId:selectedPatient.id,
        admissionId:admission?.id||form.admissionId,
        doctorId:form.doctorId||user?.id,
        admissionDate:form.admissionDate||null,
        dischargeDate:form.dischargeDate||null,
        followUpDate:form.followUpDate||null,
      };
      let saved;
      if(ds?.id){
        saved=await api.updateDischargeSummary(ds.id,payload);
      }else{
        saved=await api.createDischargeSummary(payload);
      }
      let record=saved.data||saved;
      if(finalize&&record?.id){
        const fin=await api.finalizeDischargeSummary(record.id);
        record=fin.data||record;
      }
      setDs(record);
      setForm((f:any)=>({...f,...record}));
      setEditing(record.status!=='FINALIZED');
      setMsg(finalize?t('discharge.finalizedMsg','✅ Discharge summary finalized!'):t('discharge.savedDraftMsg','✅ Discharge summary saved as draft.'));
      reloadHistory();
    }catch(e:any){setMsg('Error: '+(e.response?.data?.message||e.message));}
    finally{setSaving(false);setTimeout(()=>setMsg(''),4000);}
  };

  const addIcd=(code:string)=>{
    if(!code.trim())return;
    setForm((f:any)=>({...f,icdCodes:[...(f.icdCodes||[]),code.trim()]}));
  };
  const removeIcd=(i:number)=>setForm((f:any)=>({...f,icdCodes:f.icdCodes.filter((_:any,idx:number)=>idx!==i)}));

  const addMed=(drug?:any)=>{
    setForm((f:any)=>({...f,dischargeMedications:[...(f.dischargeMedications||[]),{
      drugName:drug?.name||drug?.drugName||'',strength:drug?.strength||'',dosage:'',frequency:'',duration:'',instructions:'',
    }]}));
  };
  const updateMed=(i:number,key:string,val:string)=>{
    setForm((f:any)=>{
      const meds=[...(f.dischargeMedications||[])];
      meds[i]={...meds[i],[key]:val};
      return {...f,dischargeMedications:meds};
    });
  };
  const removeMed=(i:number)=>setForm((f:any)=>({...f,dischargeMedications:f.dischargeMedications.filter((_:any,idx:number)=>idx!==i)}));

  const [icdInput,setIcdInput]=useState('');

  const STATUS_BADGE=(s:string)=>s==='FINALIZED'
    ?'bg-green-100 text-green-700'
    :'bg-amber-100 text-amber-700';
  // Display labels for stored enum values — only the on-screen text is translated, the actual
  // value saved to the database (form.status/conditionAtDischarge/dischargeDisposition) never
  // changes, so this is purely cosmetic and safe (unlike translating a doctor's free-text note).
  const STATUS_LABEL=(s:string)=>s==='FINALIZED'?t('discharge.statusFinalized','FINALIZED'):t('discharge.statusDraft','DRAFT');
  const CONDITION_LABEL=(o:string):string=>(({
    STABLE:t('discharge.conditionStable','STABLE'),
    IMPROVED:t('discharge.conditionImproved','IMPROVED'),
    UNCHANGED:t('discharge.conditionUnchanged','UNCHANGED'),
    DETERIORATED:t('discharge.conditionDeteriorated','DETERIORATED'),
    CRITICAL:t('discharge.conditionCritical','CRITICAL'),
  } as Record<string,string>)[o]||o);
  const DISPOSITION_LABEL=(o:string):string=>(({
    HOME:t('discharge.dispositionHome','HOME'),
    REFERRED:t('discharge.dispositionReferred','REFERRED'),
    DAMA:t('discharge.dispositionDama','DAMA'),
    LAMA:t('discharge.dispositionLama','LAMA'),
    EXPIRED:t('discharge.dispositionExpired','EXPIRED'),
    TRANSFERRED:t('discharge.dispositionTransferred','TRANSFERRED'),
  } as Record<string,string>)[o]||o);

  return(
    <Layout title={t('discharge.pageTitle','Discharge Summary')}>
      {msg&&(
        <div className={`mb-4 p-3 rounded-lg text-sm border ${msg.startsWith('✅')?'bg-green-50 text-green-700 border-green-200':msg.startsWith('Error')?'bg-red-50 text-red-700 border-red-200':'bg-blue-50 text-[#02357A] border-blue-200'}`}>
          {msg}
        </div>
      )}

      {/* Patient search */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-4">
        <PatientSearchInput
          label={t('common.patient','Patient')}
          selected={selectedPatient}
          onSelect={onPatientSelect}
          onClear={onPatientClear}
          placeholder={t('discharge.searchPatientPlaceholder','Search patient by name, UHID or phone...')}
        />
      </div>

      {!selectedPatient?(
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-16 text-center">
          <span className="text-5xl block mb-3">📝</span>
          <p className="font-semibold text-slate-600">{t('discharge.searchPrompt','Search for a patient to view or create their discharge summary')}</p>
        </div>
      ):admissionLoading?(
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center"><div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"/></div>
      ):(
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: admission + history */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
              <h3 className="font-semibold text-slate-700 text-sm mb-2">{t('discharge.currentAdmission','Current Admission')}</h3>
              {admission?(
                <div className="space-y-1 text-sm">
                  <p><span className="text-slate-400">{t('discharge.ipdNo','IPD #:')}</span> <span className="font-mono">{admission.admissionNumber||admission.id}</span></p>
                  {(admission.bedNumber||admission.bed_number)&&<p><span className="text-slate-400">{t('discharge.bed','Bed:')}</span> {admission.bedNumber||admission.bed_number}</p>}
                  {(admission.wardName||admission.ward_name)&&<p><span className="text-slate-400">{t('discharge.ward','Ward:')}</span> <AutoTranslatedText text={admission.wardName||admission.ward_name}/></p>}
                  <div className="pt-2">
                    <FileShaClaimButton
                      patientId={selectedPatient.id}
                      patientName={`${selectedPatient.firstName||selectedPatient.first_name||''} ${selectedPatient.lastName||selectedPatient.last_name||''}`.trim()}
                      patientCrId={selectedPatient.shaBeneficiaryCrId||selectedPatient.sha_beneficiary_cr_id}
                      encounterId={admission.id}
                      serviceName="Inpatient discharge"
                      serviceType="INPATIENT"
                      admissionId={admission.id}
                      from="ipd"
                    />
                  </div>
                  <p><span className="text-slate-400">{t('discharge.admitted','Admitted:')}</span> {fmtDateTime(admission.admissionDate||admission.admission_date)}</p>
                  <button onClick={startFromAdmission} disabled={saving} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2.5 px-5 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 disabled:cursor-not-allowed w-full text-xs py-2 mt-2">
                    {ds?.id?t('discharge.openSummary','📂 Open Summary for this Admission'):t('discharge.generateSummary','✨ Generate Discharge Summary')}
                  </button>
                </div>
              ):(
                <p className="text-slate-400 text-sm">{t('discharge.noActiveAdmission','No active IPD admission found for this patient. You can still create a discharge summary manually below.')}</p>
              )}
              {!admission&&!ds&&(
                <button onClick={()=>{setForm({...emptyForm(),patientId:selectedPatient.id,doctorId:user?.id||''});setEditing(true);setDs(null);}} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2.5 px-5 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50 w-full text-xs mt-2 py-2">
                  {t('discharge.startBlankSummary','✏️ Start Blank Summary')}
                </button>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
              <h3 className="font-semibold text-slate-700 text-sm mb-2">{t('discharge.pastSummaries','Past Discharge Summaries')}</h3>
              {history.length===0?(
                <p className="text-slate-400 text-xs">{t('discharge.noPastSummaries','No past summaries found')}</p>
              ):(
                <div className="space-y-2">
                  {history.map((h:any)=>(
                    <div key={h.id} onClick={()=>openExisting(h)} className={`p-2 rounded-lg border cursor-pointer hover:shadow-sm transition-all ${ds?.id===h.id?'border-blue-400 bg-blue-50':'border-slate-200'}`}>
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-mono text-slate-500">{h.summaryNumber||h.id.slice(0,8)}</p>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_BADGE(h.status)}`}>{STATUS_LABEL(h.status)}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{fmtDate(h.dischargeDate)}</p>
                      <div className="flex items-center gap-1">
                        <p className="text-xs text-slate-700 truncate flex-1"><AutoTranslatedText text={h.finalDiagnosis||'—'}/></p>
                        {h.finalDiagnosis&&(
                          <div onClick={(e:React.MouseEvent)=>e.stopPropagation()} className="flex-shrink-0 scale-75 -mr-1.5">
                            <TranslateButton text={h.finalDiagnosis}/>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-2 space-y-4">
            {!form.patientId&&!ds?(
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-16 text-center">
                <span className="text-5xl block mb-3">🛏️</span>
                <p className="font-semibold text-slate-600">{t('discharge.selectToBegin','Generate or select a discharge summary to begin')}</p>
              </div>
            ):(
              <>
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-slate-700">
                      {ds?.summaryNumber?t('discharge.summaryTitle','Discharge Summary {{number}}',{number:`#${ds.summaryNumber}`}):t('discharge.summaryTitle','Discharge Summary {{number}}',{number:t('discharge.summaryNew','(New)')})}
                    </h3>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_BADGE(form.status)}`}>{STATUS_LABEL(form.status)}</span>
                      {form.status==='FINALIZED'&&(
                        <button onClick={()=>printDischargeSummary(form,selectedPatient,user)} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2.5 px-5 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50 text-xs py-1.5 px-3">{t('discharge.print','🖨️ Print')}</button>
                      )}
                      {form.status!=='FINALIZED'&&ds?.id&&(
                        <button onClick={()=>setEditing(!editing)} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2.5 px-5 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50 text-xs py-1.5 px-3">{editing?t('discharge.view','View'):t('discharge.edit','Edit')}</button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">{t('discharge.admissionDate','Admission Date')}</label><input type="datetime-local" className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.admissionDate} disabled={!editing} onChange={e=>setForm({...form,admissionDate:e.target.value})}/></div>
                    <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">{t('discharge.dischargeDate','Discharge Date')}</label><input type="datetime-local" className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.dischargeDate} disabled={!editing} onChange={e=>setForm({...form,dischargeDate:e.target.value})}/></div>
                  </div>

                  <div className="grid grid-cols-1 gap-3">
                    <VoiceTextarea label={t('discharge.chiefComplaints','Chief Complaints')} value={form.chiefComplaints} disabled={!editing} onChange={(v:string)=>setForm({...form,chiefComplaints:v})}/>
                    <VoiceTextarea label={t('discharge.clinicalFindings','Clinical Findings')} value={form.clinicalFindings} disabled={!editing} onChange={(v:string)=>setForm({...form,clinicalFindings:v})}/>
                    <div className="grid grid-cols-2 gap-3">
                      <VoiceTextarea label={t('discharge.provisionalDiagnosis','Provisional Diagnosis')} rows={2} value={form.provisionalDiagnosis} disabled={!editing} onChange={(v:string)=>setForm({...form,provisionalDiagnosis:v})}/>
                      <VoiceTextarea label={t('discharge.finalDiagnosis','Final Diagnosis')} rows={2} value={form.finalDiagnosis} disabled={!editing} onChange={(v:string)=>setForm({...form,finalDiagnosis:v})}/>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">{t('discharge.icdCodes','ICD-11 Codes')}</label>
                      <div className="flex gap-2 flex-wrap mb-1">
                        {(form.icdCodes||[]).map((c:string,i:number)=>(
                          <span key={i} className="text-xs font-mono bg-slate-100 px-2 py-1 rounded-full flex items-center gap-1">
                            {c}{editing&&<button onClick={()=>removeIcd(i)} className="text-red-500">×</button>}
                          </span>
                        ))}
                      </div>
                      {editing&&(
                        <div className="flex gap-2">
                          <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" placeholder={t('discharge.icdPlaceholder','e.g. I10, E11.9')} value={icdInput} onChange={e=>setIcdInput(e.target.value)}
                            onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();addIcd(icdInput);setIcdInput('');}}}/>
                          <button onClick={()=>{addIcd(icdInput);setIcdInput('');}} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2.5 px-5 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50 text-xs px-3">{t('common.add','Add')}</button>
                        </div>
                      )}
                    </div>

                    <VoiceTextarea label={t('discharge.proceduresPerformed','Procedures Performed')} value={form.proceduresPerformed} disabled={!editing} onChange={(v:string)=>setForm({...form,proceduresPerformed:v})}/>
                    <VoiceTextarea label={t('discharge.hospitalCourse','Hospital Course')} rows={4} value={form.hospitalCourse} disabled={!editing} onChange={(v:string)=>setForm({...form,hospitalCourse:v})}/>
                    <VoiceTextarea label={t('discharge.investigationResults','Investigation Results')} value={form.investigationResults} disabled={!editing} onChange={(v:string)=>setForm({...form,investigationResults:v})}/>

                    {form.medicationsAdministered?.length>0&&(
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">{t('discharge.medsAdministered','Medications Administered During Stay')}</label>
                        <div className="space-y-1">
                          {form.medicationsAdministered.map((m:any,i:number)=>(
                            <div key={i} className="text-xs bg-slate-50 px-3 py-1.5 rounded-lg flex items-center justify-between">
                              <span>{m.name||m.medicationName} {m.dosage} {m.frequency&&`(${m.frequency})`}</span>
                              {m.administeredAt&&<span className="text-slate-400">{fmtDateTime(m.administeredAt)}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Discharge condition & medications */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                  <h3 className="font-semibold text-slate-700 mb-3">{t('discharge.dischargeDetails','Discharge Details')}</h3>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">{t('discharge.conditionAtDischarge','Condition at Discharge')}</label>
                      <select value={form.conditionAtDischarge} disabled={!editing}  className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" onChange={e=>setForm({...form,conditionAtDischarge:e.target.value})}>
                        {CONDITION_OPTIONS.map(o=><option key={o} value={o}>{CONDITION_LABEL(o)}</option>)}
                      </select>
                    </div>
                    <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">{t('discharge.dischargeDisposition','Discharge Disposition')}</label>
                      <select value={form.dischargeDisposition} disabled={!editing} className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" onChange={e=>setForm({...form,dischargeDisposition:e.target.value})}>
                        {DISPOSITION_OPTIONS.map(o=><option key={o} value={o}>{DISPOSITION_LABEL(o)}</option>)}
                      </select>
                    </div>
                  </div>

                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">{t('discharge.dischargeMedications','Discharge Medications')}</label>
                  {editing&&<div className="mb-2"><DrugSearchInput onSelect={addMed}/></div>}
                  {form.dischargeMedications?.length>0?(
                    <div className="space-y-2 mb-3">
                      {form.dischargeMedications.map((m:any,i:number)=>(
                        <div key={i} className="grid grid-cols-12 gap-1.5 items-center bg-slate-50 p-2 rounded-lg">
                          <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400 col-span-3 text-xs" placeholder={t('discharge.drug','Drug')} value={m.drugName||''} disabled={!editing} onChange={e=>updateMed(i,'drugName',e.target.value)}/>
                          <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400 col-span-2 text-xs" placeholder={t('discharge.dosage','Dosage')} value={m.dosage||''} disabled={!editing} onChange={e=>updateMed(i,'dosage',e.target.value)}/>
                          <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400 col-span-2 text-xs" placeholder={t('discharge.frequency','Frequency')} value={m.frequency||''} disabled={!editing} onChange={e=>updateMed(i,'frequency',e.target.value)}/>
                          <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400 col-span-2 text-xs" placeholder={t('discharge.duration','Duration')} value={m.duration||''} disabled={!editing} onChange={e=>updateMed(i,'duration',e.target.value)}/>
                          <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400 col-span-2 text-xs" placeholder={t('discharge.instructions','Instructions')} value={m.instructions||''} disabled={!editing} onChange={e=>updateMed(i,'instructions',e.target.value)}/>
                          {editing&&<button onClick={()=>removeMed(i)} className="text-red-500 text-xs col-span-1">{t('discharge.remove','Remove')}</button>}
                        </div>
                      ))}
                    </div>
                  ):<p className="text-xs text-slate-400 mb-3">{t('discharge.noDischargeMeds','No discharge medications added')}</p>}

                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <VoiceTextarea label={t('discharge.followUpInstructions','Follow-up Instructions')} value={form.followUpInstructions} disabled={!editing} onChange={(v:string)=>setForm({...form,followUpInstructions:v})}/>
                    <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">{t('discharge.followUpDate','Follow-up Date')}</label><input type="date" className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.followUpDate} disabled={!editing} onChange={e=>setForm({...form,followUpDate:e.target.value})}/></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <VoiceTextarea label={t('discharge.dietAdvice','Diet Advice')} rows={2} value={form.dietAdvice} disabled={!editing} onChange={(v:string)=>setForm({...form,dietAdvice:v})}/>
                    <VoiceTextarea label={t('discharge.activityAdvice','Activity Advice')} rows={2} value={form.activityAdvice} disabled={!editing} onChange={(v:string)=>setForm({...form,activityAdvice:v})}/>
                  </div>
                  <VoiceTextarea label={t('discharge.warningSigns','⚠️ Warning Signs — When to Seek Immediate Care')} value={form.warningSigns} disabled={!editing} onChange={(v:string)=>setForm({...form,warningSigns:v})}/>

                  {editing&&(
                    <div className="flex justify-end gap-2 mt-4">
                      <button onClick={()=>save(false)} disabled={saving} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2.5 px-5 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50">{t('common.saveDraft','💾 Save Draft')}</button>
                      <button onClick={()=>save(true)} disabled={saving} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2.5 px-5 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 disabled:cursor-not-allowed">{t('discharge.finalizeSummary','✅ Finalize Summary')}</button>
                    </div>
                  )}
                  {!editing&&ds?.id&&(
                    <div className="flex justify-end gap-2 mt-4">
                      <button onClick={()=>printDischargeSummary(form,selectedPatient,user)} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2.5 px-5 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 disabled:cursor-not-allowed">{t('discharge.printSummary','🖨️ Print Discharge Summary')}</button>
                    </div>
                  )}
                </div>
              {/* Billing Summary */}
              {admission?.id&&ds?.status==='FINALIZED'&&(
                <BillingSummaryCard admissionId={admission.id}/>
              )}
            </>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}

/* Textarea with optional disabled support. When disabled (viewing a past FINALIZED summary),
   shows the auto-translated text (cached LibreTranslate) instead of a plain read-only
   textarea — with a "Machine translated / Show original" toggle via AutoTranslatedNote, so
   the field is never silently shown translated with no way back to what was actually
   documented. Editing always shows/saves the clinician's original text untouched — this
   auto-translate treatment only ever applies to the read-only view. */
function VoiceTextarea({value,onChange,placeholder,rows=3,label,disabled}:{value:string;onChange:(v:string)=>void;placeholder?:string;rows?:number;label?:string;disabled?:boolean}){
  const {i18n}=useTranslation();
  if(disabled){
    return(
      <div>
        {label&&<label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">{label}</label>}
        <div className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl px-3.5 py-2.5 min-h-[80px] leading-relaxed">
          <AutoTranslatedNote text={value} className="text-sm text-slate-800 whitespace-pre-wrap"/>
        </div>
      </div>
    );
  }
  return(
    <div>
      {label&&<label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">{label}</label>}
      <div className="relative">
        <textarea lang={i18n.language} rows={rows} value={value||''} placeholder={placeholder}
          onChange={e=>onChange(e.target.value)} className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400 resize-y min-h-[80px] leading-relaxed pr-11"></textarea>
        <div className="absolute right-2 top-2">
          <TranslateButton text={value||''}/>
        </div>
      </div>
    </div>
  );
}
